import { Injectable } from "@angular/core";
import { BehaviorSubject, interval, Observable, Observer, Subject } from "rxjs";
import { take } from "rxjs/operators";
import { CustomizedHttpService } from "../base/customized-http.service";
import { StartUpConfigUiService } from "./start-up-config-ui.service";
import { ShowSessionExpiryModalModel, SessionStatusResponseModel } from '../../models';

@Injectable({
    providedIn: 'root'
})
export class IdleTimeoutService {
    private defaultPollingFrequencySeconds: number = 5 * 60;
    private sessionExtendedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    private sessionTimeoutId: any;
    public sessionExtended$: Observable<boolean> = this.sessionExtendedSubject.asObservable();

    private sessionExpiredSubject: Subject<boolean> = new Subject<boolean>();
    public sessionExpired$: Observable<boolean> = this.sessionExpiredSubject.asObservable();

    constructor(private httpService: CustomizedHttpService,
        private configService: StartUpConfigUiService) {
    }

    // Convert the frequency to milliseconds
    public pollingFrequencyInMillis(frequencyInSeconds: number): number {
        return ((frequencyInSeconds && frequencyInSeconds <= 0) ? this.defaultPollingFrequencySeconds : frequencyInSeconds) * 1000;
    }

    public checkToShowSessionExpiryModal(): Observable<ShowSessionExpiryModalModel> {
        return new Observable((observer: Observer<ShowSessionExpiryModalModel>) => {

            this.configService.sessionExpiryPollingFrequency$.pipe(take(1)).subscribe({
                next: (pollingFrequency) => {

                    const intervalSource$ = interval(this.pollingFrequencyInMillis(pollingFrequency));
                    const pollingIntervalSubscription = intervalSource$.subscribe({
                        next: (res) => {
                            this.httpService.get<ShowSessionExpiryModalModel>('idletimeout/showsessionexpirymodal')
                                .subscribe({
                                    next: (res) => {
                                        if (res.notifyUser) {

                                            // Stop polling to check session expiry
                                            if (pollingIntervalSubscription) {
                                                pollingIntervalSubscription.unsubscribe();
                                            }

                                            // Once Session expiry modal is shown we check for session expiry status
                                            this.checkSessionExpiryState();
                                        }
                                        observer.next(res);
                                    }
                                });
                        }
                    });

                    return {
                        unsubscribe() {
                            if (pollingIntervalSubscription) {
                                pollingIntervalSubscription.unsubscribe();
                            }
                        }
                    }

                }
            });

        });
    }

    public tryExtendSession(): Observable<boolean> {
        return new Observable<boolean>((observer: Observer<boolean>) => {
            const extendSessionSub =
                this.extendSession()
                    .subscribe({
                        next: (isSessionExtended) => {
                            this.sessionExtendedSubject.next(isSessionExtended);
                            if (isSessionExtended) {
                                clearTimeout(this.sessionTimeoutId);
                            }
                            observer.next(isSessionExtended);
                        },
                        error: (err: any) => {
                            this.sessionExtendedSubject.next(false);
                            observer.next(false);
                        }
                    });
            return {
                unsubscribe() {
                    if (extendSessionSub) {
                        extendSessionSub.unsubscribe();
                    }
                }
            }
        });
    }

    private checkSessionExpiryState(): void {
        // Poll and check if session is expired
        this.httpService.get<SessionStatusResponseModel>('idletimeout/isSessionExpired')
            .pipe(take(1))
            .subscribe({
                next: (res) => {
                    if (res && res.isExpired) {
                        // Save Session Expiry counter
                        this.sessionExpiredSubject.next(true);
                        clearTimeout(this.sessionTimeoutId);
                        return;
                    }

                    if (this.sessionTimeoutId) {
                        clearTimeout(this.sessionTimeoutId);
                    }

                    this.sessionTimeoutId = setTimeout(() => {
                        this.checkSessionExpiryState();
                        return;
                    }, 3000);
                }
            });
    }

    private extendSession(): Observable<boolean> {
        return this.httpService.post<any, boolean>('idletimeout/extendsession', {});
    }
}
